/**
 * Client Service
 *
 * Handles client-related data operations including fetching client lists
 * for multi-tenant administration features.
 */

import { API, graphqlOperation } from 'aws-amplify';
import { listClients as listClientsQuery } from '../graphql/queries';
import * as logger from '../utils/logger';

/**
 * Fetches all clients with pagination and alphabetical sorting
 *
 * @returns {Promise<Array>} Array of client objects sorted by clientName
 * @throws {Error} If the fetch operation fails
 *
 * @example
 * const clients = await listClients();
 * // Returns: [{ clientId: 'grm', clientName: 'GRM Restaurant Group' }, ...]
 */
export const listClients = async () => {
  let allClients = [];
  let nextToken = null;
  let iterations = 0;
  const maxIterations = 50; // Safety limit to prevent infinite loops

  try {
    do {
      const response = await API.graphql(
        graphqlOperation(listClientsQuery, {
          limit: 1000,
          nextToken
        })
      );

      const items = response?.data?.listClients?.items || [];
      allClients = [...allClients, ...items];
      nextToken = response?.data?.listClients?.nextToken;
      iterations++;

      // Safety check for infinite pagination
      if (iterations >= maxIterations) {
        logger.warn('Reached maximum fetch limit while fetching clients', {
          totalItems: allClients.length,
          iterations
        });
        break;
      }
    } while (nextToken);

    // Sort alphabetically by clientName
    allClients.sort((a, b) =>
      a.clientName.localeCompare(b.clientName)
    );

    logger.debug('Successfully fetched all clients', {
      totalItems: allClients.length,
      iterations
    });

    return allClients;
  } catch (error) {
    logger.error('Failed to fetch clients', {
      error,
      iterations,
      partialResults: allClients.length
    });
    throw new Error('Unable to fetch clients. Please try again.');
  }
};
